import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Star, Plus, Trash2 } from "lucide-react";

function StarPicker({ value, onChange, name }) {
    return (
        <div className="inline-flex items-center gap-0.5">
            {[1,2,3,4,5].map((n) => (
                <button
                    type="button"
                    key={n}
                    onClick={() => onChange(n === value ? 0 : n)}
                    className="text-amber-500 hover:scale-110 transition-transform"
                    data-testid={`star-${name}-${n}`}
                >
                    <Star className={`w-4 h-4 ${n <= value ? "fill-amber-500" : "fill-none"}`} />
                </button>
            ))}
        </div>
    );
}

function SkillRow({ label, value, onChange, onRemove, testIdPrefix }) {
    return (
        <div className="flex items-center justify-between gap-3 py-2">
            <span className="text-sm font-medium flex-1 truncate" title={label}>{label}</span>
            <div className="flex items-center gap-2">
                <StarPicker value={value || 0} onChange={onChange} name={`${testIdPrefix}-${label}`} />
                {onRemove && (
                    <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-destructive" data-testid={`remove-${testIdPrefix}-${label}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

export default function CandidateRatings({ candidateId, currentUserId, candidateSkills = [] }) {
    const [data, setData] = useState({ ratings: [], aggregate: { hard_skills: {}, soft_skills: {}, critical: {}, count: 0 }, default_critical: [] });
    const [draft, setDraft] = useState({
        hard_skills: {}, soft_skills: {}, critical: {}, overall: 0, recommendation: "", summary: "",
    });
    const [newHard, setNewHard] = useState("");
    const [newSoft, setNewSoft] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/ratings`);
            setData(data);
            // Prefill draft with current user's rating, if any
            const mine = (data.ratings || []).find((r) => r.author_id === currentUserId);
            if (mine) {
                setDraft({
                    hard_skills: mine.hard_skills || {},
                    soft_skills: mine.soft_skills || {},
                    critical: mine.critical || {},
                    overall: mine.overall || 0,
                    recommendation: mine.recommendation || "",
                    summary: mine.summary || "",
                });
            } else {
                // seed critical defaults
                const seed = {};
                (data.default_critical || []).forEach((k) => (seed[k] = 0));
                setDraft((d) => ({ ...d, critical: seed }));
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

    const setVal = (group, key, v) => setDraft((d) => ({ ...d, [group]: { ...d[group], [key]: v } }));
    const removeKey = (group, key) => setDraft((d) => {
        const copy = { ...d[group] };
        delete copy[key];
        return { ...d, [group]: copy };
    });

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/ratings`, {
                hard_skills: draft.hard_skills,
                soft_skills: draft.soft_skills,
                critical: draft.critical,
                overall: draft.overall || null,
                recommendation: draft.recommendation || null,
                summary: draft.summary || null,
            });
            toast.success("Your rating saved");
            await load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const recOpts = ["Strong Hire", "Hire", "Lean Hire", "Lean No", "No Hire"];
    const allHardKeys = Array.from(new Set([
        ...Object.keys(draft.hard_skills || {}),
        ...candidateSkills.slice(0, 5),
    ]));

    return (
        <div className="grid md:grid-cols-3 gap-px bg-border">
            {/* My rating */}
            <div className="bg-white p-6 md:col-span-2 space-y-6">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Your assessment</div>
                    <h3 className="font-display font-bold text-xl mt-1">Rate this candidate</h3>
                </div>

                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Hard skills (1–5)</div>
                    {allHardKeys.length === 0 && <div className="text-xs text-muted-foreground">Add a skill below.</div>}
                    {allHardKeys.map((k) => (
                        <SkillRow
                            key={k}
                            label={k}
                            value={draft.hard_skills[k] || 0}
                            onChange={(v) => setVal("hard_skills", k, v)}
                            onRemove={() => removeKey("hard_skills", k)}
                            testIdPrefix="hard"
                        />
                    ))}
                    <div className="flex gap-2 mt-3">
                        <Input value={newHard} onChange={(e) => setNewHard(e.target.value)} placeholder="Add hard skill" className="rounded-none h-9" data-testid="add-hard-skill" />
                        <Button type="button" size="sm" variant="outline" className="rounded-none h-9" onClick={() => { if (newHard.trim()) { setVal("hard_skills", newHard.trim(), 0); setNewHard(""); } }}>
                            <Plus className="w-3 h-3" />
                        </Button>
                    </div>
                </section>

                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Soft skills (1–5)</div>
                    {["Communication","Ownership","Collaboration","Adaptability"].concat(Object.keys(draft.soft_skills)).filter((v, i, a) => a.indexOf(v) === i).map((k) => (
                        <SkillRow
                            key={k}
                            label={k}
                            value={draft.soft_skills[k] || 0}
                            onChange={(v) => setVal("soft_skills", k, v)}
                            testIdPrefix="soft"
                        />
                    ))}
                    <div className="flex gap-2 mt-3">
                        <Input value={newSoft} onChange={(e) => setNewSoft(e.target.value)} placeholder="Add soft skill" className="rounded-none h-9" data-testid="add-soft-skill" />
                        <Button type="button" size="sm" variant="outline" className="rounded-none h-9" onClick={() => { if (newSoft.trim()) { setVal("soft_skills", newSoft.trim(), 0); setNewSoft(""); } }}>
                            <Plus className="w-3 h-3" />
                        </Button>
                    </div>
                </section>

                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Critical parameters (1–5)</div>
                    {Object.keys(draft.critical).length === 0 && (data.default_critical || []).map((k) => (
                        <SkillRow key={k} label={k} value={0} onChange={(v) => setVal("critical", k, v)} testIdPrefix="critical" />
                    ))}
                    {Object.keys(draft.critical).map((k) => (
                        <SkillRow key={k} label={k} value={draft.critical[k] || 0} onChange={(v) => setVal("critical", k, v)} testIdPrefix="critical" />
                    ))}
                </section>

                <section className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Overall (1–10)</Label>
                        <Input type="number" min={1} max={10} value={draft.overall || ""} onChange={(e) => setDraft({ ...draft, overall: parseInt(e.target.value) || 0 })} className="rounded-none mt-2 h-10" data-testid="rating-overall" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Recommendation</Label>
                        <select value={draft.recommendation} onChange={(e) => setDraft({ ...draft, recommendation: e.target.value })} className="mt-2 h-10 w-full px-3 border border-border bg-white text-sm" data-testid="rating-recommendation">
                            <option value="">—</option>
                            {recOpts.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                </section>
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Notes / Summary</Label>
                    <Textarea rows={3} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} className="rounded-none mt-2" data-testid="rating-summary" />
                </div>
                <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="save-rating">{busy ? "Saving..." : "Save my rating"}</Button>
            </div>

            {/* Team panel */}
            <div className="bg-white p-6 space-y-5">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Team rating</div>
                    <div className="flex items-baseline gap-2 mt-1">
                        <div className="font-display font-black text-5xl">{data.aggregate.overall ?? "—"}</div>
                        <div className="text-sm text-muted-foreground">/ 10 · {data.aggregate.count} rater{data.aggregate.count === 1 ? "" : "s"}</div>
                    </div>
                </div>
                <Section label="Hard skills avg" obj={data.aggregate.hard_skills} />
                <Section label="Soft skills avg" obj={data.aggregate.soft_skills} />
                <Section label="Critical avg" obj={data.aggregate.critical} />

                <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Individual ratings ({(data.ratings || []).length})</div>
                    {(data.ratings || []).length === 0 ? (
                        <div className="text-xs text-muted-foreground">No ratings yet</div>
                    ) : (
                        <div className="space-y-3" data-testid="individual-ratings-list">
                            {data.ratings.map((r) => (
                                <RatingDetailCard key={r.id} r={r} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function RatingDetailCard({ r }) {
    const [open, setOpen] = useState(false);
    const hardEntries = Object.entries(r.hard_skills || {}).filter(([, v]) => v);
    const softEntries = Object.entries(r.soft_skills || {}).filter(([, v]) => v);
    const critEntries = Object.entries(r.critical || {}).filter(([, v]) => v);
    const role = r.author?.role;
    const recColor = (r.recommendation || "").toLowerCase().includes("hire")
        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
        : (r.recommendation || "").toLowerCase().includes("no")
            ? "border-rose-300 bg-rose-50 text-rose-800"
            : "border-border bg-secondary text-foreground/70";
    return (
        <div className="border border-border bg-white" data-testid={`rating-card-${r.author_id}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-secondary/30"
                data-testid={`rating-toggle-${r.author_id}`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold shrink-0">
                        {(r.author?.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{r.author?.name || "Unknown"}</div>
                        {role && <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">{role}</div>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-sm font-bold tabular-nums">{r.overall ?? "—"}/10</span>
                    {r.recommendation && <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${recColor}`}>{r.recommendation}</span>}
                    <span className="text-foreground/40">{open ? "−" : "+"}</span>
                </div>
            </button>
            {open && (
                <div className="border-t border-border px-3 py-3 space-y-3 bg-secondary/10" data-testid={`rating-detail-${r.author_id}`}>
                    {r.summary && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Notes</div>
                            <p className="text-xs leading-relaxed whitespace-pre-wrap">{r.summary}</p>
                        </div>
                    )}
                    {hardEntries.length > 0 && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Hard skills</div>
                            <RatingSkillGrid entries={hardEntries} />
                        </div>
                    )}
                    {softEntries.length > 0 && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Soft skills</div>
                            <RatingSkillGrid entries={softEntries} />
                        </div>
                    )}
                    {critEntries.length > 0 && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Critical parameters</div>
                            <RatingSkillGrid entries={critEntries} />
                        </div>
                    )}
                    {r.updated_at && (
                        <div className="text-[10px] font-mono text-foreground/40 pt-1 border-t border-border">
                            {new Date(r.updated_at).toLocaleString()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function RatingSkillGrid({ entries }) {
    return (
        <div className="grid grid-cols-2 gap-y-1 gap-x-3">
            {entries.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                    <span className="truncate">{k}</span>
                    <div className="inline-flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className={`w-3 h-3 ${n <= v ? "text-amber-500 fill-amber-500" : "text-foreground/15"}`} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Section({ label, obj }) {
    const entries = Object.entries(obj || {});
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-1">{label}</div>
            {entries.length === 0 ? (
                <div className="text-xs text-muted-foreground">—</div>
            ) : (
                <div className="space-y-1">
                    {entries.map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-xs">
                            <span className="truncate">{k}</span>
                            <span className="font-mono font-medium">{v}/5</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
